import React, { FC, useState } from 'react';
import { useQuery } from 'react-query';
import { QueryKey } from 'api/queryKey';
import { Input, Modal, Select } from 'antd';
import { commonPaginationConfig, initialPageParams } from 'models/page';
import { ModalProps } from 'antd/lib/modal';
import { DevicesAPI, DevicesFetchFilters } from 'api/DevicesAPI';
import Column from 'antd/lib/table/Column';
import { AssignDeviceToPartnerButton } from 'common/components/AssignDeviceToBusinessPartnerButton';
import { DeviceModeType, deviceModeTypeLabelMap } from 'models/higoDevice';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { FormattedMessage } from 'react-intl';
import { NumberedTable } from 'common/components/NumberedTable';
import { useRole } from 'common/hooks/useRole';
import { BusinessPartnerContext } from 'pages/BusinessPartnerManagement/BusinessPartnerContext';
import { useEnsureContext } from 'common/hooks';
import { isNil } from 'ramda';
import styled from 'styled-components';

const StyledModal = styled(Modal)`
  width: 70% !important;
`;

const { Search } = Input;
const { Option } = Select;
export interface AssignDeviceToBusinessPartnerModalProps extends ModalProps {
  businessPartnerId: number;
}

export const AssignDeviceToBusinessPartnerModal: FC<AssignDeviceToBusinessPartnerModalProps> = ({
  businessPartnerId,
  ...modalProps
}) => {
  const [pageMetaData, setPageMetaData] = useState(initialPageParams);
  const [filters, setFilters] = useState<DevicesFetchFilters>({
    assigned: false,
  });
  const [deviceModeType, setDeviceModeType] = useState<DeviceModeType>(
    DeviceModeType.FamilyMode
  );
  const bpContext = useEnsureContext(BusinessPartnerContext);

  const { isMedicalOperator } = useRole();
  const shouldHidePartnerColumn =
    !isNil(bpContext.parentPartnerId) || isMedicalOperator;
  const { isFetching, data } = useQuery(
    [QueryKey.SelectDeviceModalDevicesList, pageMetaData, filters],
    () =>
      DevicesAPI.fetch({
        ...pageMetaData,
        ...filters,
      }),
    {
      keepPreviousData: true,
    }
  );

  const intlMessage = useFormatMessage();

  const paginationConfig = {
    ...commonPaginationConfig,
    current: pageMetaData?.page,
    pageSize: data?.size,
    total: data?.totalElements,
    onChange: (page: number, pageSize?: number) =>
      setPageMetaData((prev) => ({
        ...prev,
        page,
        ...(pageSize && { pageSize }),
      })),
  };

  const deviceModeTypeOptions = Object.values(DeviceModeType).map((mode) => (
    <Option key={mode} value={mode}>
      {intlMessage({ id: deviceModeTypeLabelMap.get(mode) ?? mode })}
    </Option>
  ));

  return (
    <StyledModal
      centered={true}
      maskClosable={false}
      destroyOnClose={true}
      {...modalProps}
    >
      <Search
        placeholder={intlMessage({ id: 'devices.serialNumber.placeholder' })}
        onSearch={(value) => {
          setPageMetaData((pageMetaData) => ({ ...pageMetaData, page: 1 }));
          setFilters((filters) => ({
            ...filters,
            serialNumber: value,
            boxSerialNumber: value,
          }));
        }}
        style={{ width: '100%', marginBottom: '1rem' }}
      />
      <FormattedMessage id="devices.mode" tagName="h4" />
      <Select
        value={deviceModeType}
        onSelect={(value) => setDeviceModeType(value)}
        style={{ width: '100%', marginBottom: '1rem' }}
      >
        {deviceModeTypeOptions}
      </Select>
      <NumberedTable
        rowKey="id"
        size="middle"
        loading={isFetching}
        dataSource={data?.content}
        pagination={paginationConfig}
      >
        <Column
          title={intlMessage({ id: 'deviceSerialNumber' })}
          dataIndex="serialNumber"
        />
        <Column
          title={intlMessage({ id: 'devices.boxSerialNumber' })}
          dataIndex="boxSerialNumber"
        />
        {!shouldHidePartnerColumn && (
          <Column
            title={intlMessage({ id: 'partner' })}
            dataIndex="businessPartner"
          />
        )}
        <Column
          width="170px"
          align="right"
          render={(record) => (
            <AssignDeviceToPartnerButton
              key="1"
              type="primary"
              businessPartnerId={businessPartnerId}
              deviceId={record.id}
              deviceModeType={deviceModeType}
            >
              <FormattedMessage id="assign" />
            </AssignDeviceToPartnerButton>
          )}
        />
      </NumberedTable>
    </StyledModal>
  );
};
